import { api } from './api';
import type {
  Job,
  JobSummary,
  JobApplication,
  ApplicationMessage,
  SavedJob,
  JobSearchParams,
  JobSearchResult,
  ApplyToJobRequest,
  UpdateApplicationStatusRequest,
  SendMessageRequest,
} from '../types/job';

// ============================================================================
// Job Search & Browse
// ============================================================================

export async function searchJobs(params: JobSearchParams): Promise<JobSearchResult> {
  const searchParams = new URLSearchParams();

  if (params.q) searchParams.set('q', params.q);
  if (params.category) searchParams.set('category', params.category);
  if (params.city) searchParams.set('city', params.city);
  if (params.salary_min) searchParams.set('salary_min', params.salary_min.toString());
  if (params.salary_max) searchParams.set('salary_max', params.salary_max.toString());
  if (params.company) searchParams.set('company', params.company);
  if (params.featured) searchParams.set('featured', 'true');
  if (params.page) searchParams.set('page', params.page.toString());
  if (params.limit) searchParams.set('limit', params.limit.toString());
  if (params.sort) searchParams.set('sort', params.sort);
  if (params.order) searchParams.set('order', params.order);

  // Array params
  params.job_type?.forEach(t => searchParams.append('job_type', t));
  params.experience_level?.forEach(l => searchParams.append('experience_level', l));
  params.location_type?.forEach(l => searchParams.append('location_type', l));
  params.skill?.forEach(s => searchParams.append('skill', s));

  const response = await api.get<JobSearchResult>(`/api/v1/jobs?${searchParams.toString()}`);
  return response.data;
}

export async function getJob(idOrSlug: string): Promise<Job> {
  const response = await api.get<Job>(`/api/v1/jobs/${idOrSlug}`);
  return response.data;
}

export async function getCompanyJobs(companyId: string, status?: string): Promise<JobSummary[]> {
  const params = status ? `?status=${status}` : '';
  const response = await api.get<{ jobs: JobSummary[] }>(`/api/v1/companies/${companyId}/jobs${params}`);
  return response.data.jobs;
}

// ============================================================================
// Job Management (Company Only)
// ============================================================================

export async function createJob(data: Partial<Job>): Promise<Job> {
  const response = await api.post<Job>('/api/v1/jobs', data);
  return response.data;
}

export async function updateJob(jobId: string, data: Partial<Job>): Promise<void> {
  await api.put(`/api/v1/jobs/${jobId}`, data);
}

export async function deleteJob(jobId: string): Promise<void> {
  await api.delete(`/api/v1/jobs/${jobId}`);
}

export async function publishJob(jobId: string): Promise<void> {
  await api.post(`/api/v1/jobs/${jobId}/publish`);
}

export async function pauseJob(jobId: string): Promise<void> {
  await api.post(`/api/v1/jobs/${jobId}/pause`);
}

export async function closeJob(jobId: string): Promise<void> {
  await api.post(`/api/v1/jobs/${jobId}/close`);
}

export async function getMyJobs(status?: string): Promise<JobSummary[]> {
  const params = status ? `?status=${status}` : '';
  const response = await api.get<{ jobs: JobSummary[] }>(`/api/v1/jobs/mine${params}`);
  return response.data.jobs;
}

export async function getJobApplications(
  jobId: string,
  status?: string
): Promise<JobApplication[]> {
  const params = status ? `?status=${status}` : '';
  const response = await api.get<{ applications: JobApplication[] }>(
    `/api/v1/jobs/${jobId}/applications${params}`
  );
  return response.data.applications;
}

// ============================================================================
// Applications (Expert)
// ============================================================================

export async function applyToJob(jobId: string, data: ApplyToJobRequest): Promise<JobApplication> {
  const response = await api.post<JobApplication>(`/api/v1/jobs/${jobId}/apply`, data);
  return response.data;
}

export async function getMyApplications(status?: string): Promise<JobApplication[]> {
  const params = status ? `?status=${status}` : '';
  const response = await api.get<{ applications: JobApplication[] }>(`/api/v1/applications${params}`);
  return response.data.applications;
}

export async function withdrawApplication(applicationId: string): Promise<void> {
  await api.post(`/api/v1/applications/${applicationId}/withdraw`);
}

// ============================================================================
// Applications (Company)
// ============================================================================

export async function updateApplicationStatus(
  applicationId: string,
  data: UpdateApplicationStatusRequest
): Promise<void> {
  await api.put(`/api/v1/applications/${applicationId}/status`, data);
}

// ============================================================================
// Messages
// ============================================================================

export async function getMessages(applicationId: string): Promise<ApplicationMessage[]> {
  const response = await api.get<{ messages: ApplicationMessage[] }>(
    `/api/v1/applications/${applicationId}/messages`
  );
  return response.data.messages;
}

export async function sendMessage(
  applicationId: string,
  data: SendMessageRequest
): Promise<ApplicationMessage> {
  const response = await api.post<ApplicationMessage>(
    `/api/v1/applications/${applicationId}/messages`,
    data
  );
  return response.data;
}

// ============================================================================
// Saved Jobs (Expert)
// ============================================================================

export async function saveJob(jobId: string, notes?: string): Promise<void> {
  await api.post(`/api/v1/jobs/${jobId}/save`, { notes });
}

export async function unsaveJob(jobId: string): Promise<void> {
  await api.delete(`/api/v1/jobs/${jobId}/save`);
}

export async function getSavedJobs(): Promise<SavedJob[]> {
  const response = await api.get<{ saved_jobs: SavedJob[] }>('/api/v1/jobs/saved');
  return response.data.saved_jobs;
}
